import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Sparkles, Target, Users, Zap, TrendingUp, Award, Heart, Lightbulb, ArrowRight } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Link } from 'react-router-dom';

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>About Us - AI Mastery Hub | Empowering Your AI Journey</title>
        <meta name="description" content="Discover how AI Mastery Hub is transforming lives through cutting-edge AI education. Join thousands of learners mastering the future." />
      </Helmet>

      {/* Navigation */}
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-blue-900/20"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(139, 92, 246, 0.15) 0%, transparent 50%)',
        }}></div>

        <div className="max-w-7xl mx-auto relative z-10 text-center">
          <div className="inline-block px-4 py-2 bg-purple-600/20 border border-purple-500/30 rounded-full text-sm mb-6">
            <span className="text-purple-400">✨ Transforming Lives Through AI</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6">
            <span className="block">Welcome to</span>
            <span className="block bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
              AI Mastery Hub
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Where your journey to AI mastery begins. We believe in the infinite potential within you
            to create, innovate, and thrive in the age of artificial intelligence.
          </p>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 px-6 bg-gradient-to-br from-purple-900/10 via-black to-blue-900/10">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-12 border border-white/20 text-center">
            <Sparkles className="w-16 h-16 mx-auto mb-6 text-purple-400" />
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Mission</h2>
            <p className="text-xl text-gray-300 leading-relaxed max-w-4xl mx-auto">
              To empower individuals worldwide to harness the transformative power of AI,
              unlocking new opportunities for success, creativity, and abundance. We believe that
              mastering AI isn't just about learning technology—it's about <span className="text-purple-400 font-semibold">manifesting your highest potential</span> and
              creating the future you deserve.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                The Journey to <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Mastery</span>
              </h2>
              <div className="space-y-4 text-lg text-gray-300">
                <p>
                  AI Mastery Hub was born from a simple yet powerful vision: <span className="text-white font-semibold">everyone deserves access to world-class AI education</span>
                  that doesn't just teach skills, but transforms lives.
                </p>
                <p>
                  We've seen firsthand how AI knowledge can unlock doors that once seemed impossible to open.
                  From entrepreneurs launching six-figure businesses to career professionals commanding higher salaries,
                  to creatives bringing their visions to life—AI mastery creates <span className="text-purple-400 font-semibold">limitless possibilities</span>.
                </p>
                <p>
                  We're not just another online course platform. We're your partner in transformation,
                  your guide to abundance, and your catalyst for success in the AI revolution.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl blur-3xl opacity-30"></div>
              <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 border border-white/20">
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-purple-400 mb-2">10,000+</div>
                    <div className="text-sm text-gray-400">Lives Transformed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-blue-400 mb-2">500+</div>
                    <div className="text-sm text-gray-400">Success Stories</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-cyan-400 mb-2">98%</div>
                    <div className="text-sm text-gray-400">Satisfaction Rate</div>
                  </div>
                  <div className="text-center">
                    <div className="text-4xl font-bold text-green-400 mb-2">24/7</div>
                    <div className="text-sm text-gray-400">Learning Access</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-900/10 via-black to-purple-900/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">What We Stand For</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Our values guide everything we do, ensuring your success is always our priority.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Value 1 */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/20 hover:border-purple-500/50 transition-all">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl flex items-center justify-center mb-6">
                <Heart className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Empowerment</h3>
              <p className="text-gray-400">
                We believe in your unlimited potential. Our courses are designed to unlock your inner genius
                and help you create the success you envision.
              </p>
            </div>

            {/* Value 2 */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/20 hover:border-blue-500/50 transition-all">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl flex items-center justify-center mb-6">
                <Lightbulb className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Innovation</h3>
              <p className="text-gray-400">
                We stay at the cutting edge of AI, bringing you the latest tools and strategies
                to stay ahead in a rapidly evolving world.
              </p>
            </div>

            {/* Value 3 */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/20 hover:border-cyan-500/50 transition-all">
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-600 to-green-600 rounded-2xl flex items-center justify-center mb-6">
                <TrendingUp className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Abundance</h3>
              <p className="text-gray-400">
                We approach education with an abundance mindset. When you succeed, we all succeed.
                Your wins are our wins.
              </p>
            </div>

            {/* Value 4 */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/20 hover:border-green-500/50 transition-all">
              <div className="w-16 h-16 bg-gradient-to-r from-green-600 to-emerald-600 rounded-2xl flex items-center justify-center mb-6">
                <Users className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Community</h3>
              <p className="text-gray-400">
                You're joining a tribe of like-minded visionaries, all committed to growth,
                learning, and supporting each other's journey.
              </p>
            </div>

            {/* Value 5 */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/20 hover:border-purple-500/50 transition-all">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center mb-6">
                <Award className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Excellence</h3>
              <p className="text-gray-400">
                We deliver only the highest quality content, taught by experts who are
                actively succeeding in the AI space.
              </p>
            </div>

            {/* Value 6 */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/20 hover:border-blue-500/50 transition-all">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <Zap className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-4">Action</h3>
              <p className="text-gray-400">
                Knowledge without action is just information. We focus on practical,
                implementable strategies that create real results.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Why <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">AI Mastery Hub</span>?
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              We're not just teaching AI—we're creating a movement of empowered individuals who are ready to thrive.
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/20 hover:border-purple-500/50 transition-all">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-3">Results-Focused Curriculum</h3>
                  <p className="text-gray-400 text-lg">
                    Every course is designed with one goal: your success. We don't waste time on theory that doesn't translate to
                    real-world results. You'll learn practical skills you can implement immediately to start seeing results.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/20 hover:border-blue-500/50 transition-all">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-3">Cutting-Edge Content</h3>
                  <p className="text-gray-400 text-lg">
                    AI evolves at lightning speed, and so do we. Our courses are constantly updated with the latest tools,
                    techniques, and strategies to ensure you're always ahead of the curve.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/20 hover:border-cyan-500/50 transition-all">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-cyan-600 to-green-600 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-3">Supportive Community</h3>
                  <p className="text-gray-400 text-lg">
                    You're not alone on this journey. Connect with thousands of fellow learners, share wins,
                    get support, and build relationships that last a lifetime.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-2xl p-8 border border-white/20 hover:border-green-500/50 transition-all">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-3">Lifetime Value</h3>
                  <p className="text-gray-400 text-lg">
                    This isn't just a course—it's an investment in yourself that pays dividends for life.
                    The skills you learn here will create opportunities and income for years to come.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Transformation Promise */}
      <section className="py-20 px-6 bg-gradient-to-br from-purple-900/20 via-black to-blue-900/20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl blur-3xl opacity-20"></div>
            <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-12 border border-white/20">
              <Sparkles className="w-16 h-16 mx-auto mb-6 text-purple-400" />
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Promise to You</h2>
              <p className="text-xl text-gray-300 leading-relaxed mb-8">
                When you join AI Mastery Hub, you're not just enrolling in courses—you're stepping into
                a <span className="text-purple-400 font-semibold">new chapter of your life</span>. A chapter filled with possibility, growth, and success.
                We promise to meet you where you are, guide you every step of the way, and celebrate your
                victories as you <span className="text-blue-400 font-semibold">manifest the future you deserve</span>.
              </p>
              <p className="text-2xl font-bold text-transparent bg-gradient-to-r from-purple-400 via-blue-400 to-cyan-400 bg-clip-text mb-8">
                Your success is our mission. Your growth is our purpose.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Begin Your <span className="bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">Transformation</span>?
          </h2>
          <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
            The future you've been dreaming of is waiting. Take the first step toward AI mastery today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/courses"
              className="group bg-gradient-to-r from-purple-600 to-blue-600 px-8 py-4 rounded-full font-semibold hover:shadow-2xl hover:shadow-purple-500/50 transition-all text-lg inline-flex items-center justify-center"
            >
              Explore Courses
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/contact"
              className="group bg-white/10 backdrop-blur-sm border border-white/20 px-8 py-4 rounded-full font-semibold hover:bg-white/20 transition-all text-lg inline-flex items-center justify-center"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
